import React, { useState } from 'react';
import { animated } from '@react-spring/web';
import { useNavigate } from 'react-router';

import Modal from '~/components/modals/modal';
import MagicLinkModalForm from '~/components/modals/funnel-auth/magic-link-modal-form';
import { Page } from '~/components/modals/store-auth/store-auth-modal';
import { api } from '~/api';
import { useModal } from '~/features/modals/modal-actions';
import { useAnimatedPageTransitions } from '~/hooks/use-animated-page-transitions';
import {
  accountSettingsRoute,
  planDetailsRoute,
  yardPlanPageRoute,
} from '~/routes';
import { captureException } from '~/utils/exception-tracking';
import { useVwoExperiment } from '~/hooks/use-vwo-experiment';
import { MULTICATEGORY_PSP_TEST } from '~/utils/vwo-tests';

import styles from '~/components/modals/modal.module.scss';

const MagicLinkModal = ({ isOpen, magicLinkRedirectUrl, onClose }) => {
  const { getVariantName } = useVwoExperiment(MULTICATEGORY_PSP_TEST.NAME, {
    activateOnLoad: false,
  });
  const multiCategoryPspVariant = getVariantName();
  const showMultiCategoryPsp =
    multiCategoryPspVariant ===
    MULTICATEGORY_PSP_TEST.MULTICATEGORY_PSP_VARIANT;
  const { showModal } = useModal();
  const [email, setEmail] = useState();
  const navigate = useNavigate();
  const { transitions, transitionTo, resetPage } = useAnimatedPageTransitions(
    Page.SIGN_IN
  );

  const handleCloseModal = () => {
    resetPage();
    setEmail(null);
    onClose();
  };

  const handleRequestMagicLink = (email) => {
    return api.sessions
      .requestMagicLink({
        email,
        queryParams: { redirect: magicLinkRedirectUrl },
        source: 'lpsp',
      })
      .then(() => {
        setEmail(email);
        transitionTo(Page.MAGIC_LINK, 1);
      })
      .catch(() => showModal('ERROR'));
  };

  return (
    <Modal onClose={handleCloseModal} isOpen={isOpen}>
      {transitions((styleProps, item) => {
        switch (item) {
          case Page.SIGN_IN:
            return (
              <animated.div style={styleProps} className={styles.container}>
                <h1 className={styles.title}>
                  Let's pick up where we left off
                </h1>
                <p className={styles.text} style={{ textAlign: 'center' }}>
                  Welcome back! If you have an email in the Sunday system, we'll
                  send you a link to quickly access your custom plans.
                </p>
                <MagicLinkModalForm
                  initialValues={{ email }}
                  onSubmit={({ email }) => handleRequestMagicLink(email)}
                  onSignInWithThirdParty={async () => {
                    let lawns;
                    try {
                      lawns = await api.lawns.all();
                    } catch (error) {
                      captureException(
                        new Error('Error fetching lawn after 3rd party login'),
                        {
                          extras: {
                            errorInfo: JSON.stringify(error),
                          },
                        }
                      );
                    }
                    if (lawns && lawns?.length > 0) {
                      navigate(
                        showMultiCategoryPsp
                          ? yardPlanPageRoute(lawns[0].uuid)
                          : planDetailsRoute(lawns[0].uuid)
                      );
                    } else {
                      navigate(accountSettingsRoute());
                    }

                    handleCloseModal();
                  }}
                />
              </animated.div>
            );

          case Page.MAGIC_LINK:
            return (
              <animated.div style={styleProps} className={styles.container}>
                <h1 className={styles.title}>Check your email!</h1>
                <img
                  src="/icons/checkmark-circle.svg?v3"
                  alt=""
                  width="24"
                  height="24"
                  style={{
                    width: '3.2rem',
                    height: 'auto',
                    display: 'block',
                    margin: '-2.4rem auto 3.2rem auto',
                  }}
                />
                <p className={styles.text} style={{ textAlign: 'center' }}>
                  We're sending a link to <strong>{email}</strong>.
                  <br />
                  It'll be there shortly!
                </p>
              </animated.div>
            );

          default:
            return null;
        }
      })}
    </Modal>
  );
};

export default MagicLinkModal;
